import React, { forwardRef } from 'react';
import { Box, Divider, Text } from '@mantine/core';
import moment from 'moment';
import { Message } from './Message';
import type { SlackMessage } from '../../../api/slackConversation';

const DAY_DIVIDER_COLOR = '#E1E4E8';

export interface ExcerptRange {
  excerptFirstChar: number;
  excerptLastChar: number;
  excerpt: string;
  entity?: string;
}

interface MessageDayProps {
  date: string;
  messages: SlackMessage[];
  channelName: string;
  excerptMessageTsHighlight?: Set<string>;
  excerptRefIndexByMessageTs?: Map<string, number>;
  excerptTextsByMessageTs?: Map<string, string[]>;
  excerptRefsArray?: React.MutableRefObject<(HTMLDivElement | null)[]>;
}

function formatDayLabel(dateStr: string): string {
  if (dateStr.includes('_t_')) {
    const datePart = dateStr.split('_t_')[0];
    if (datePart) {
      const d = moment(datePart);
      if (d.isValid()) return `Thread (${d.format('MMM D, YYYY')})`;
    }
    return 'Thread';
  }
  const d = moment(dateStr);
  const today = moment();
  if (d.isSame(today, 'day')) return 'Today';
  if (d.isSame(today.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
  if (d.isSame(today, 'year')) return d.format('MMMM D');
  return d.format('MMMM D, YYYY');
}

export const MessageDay = forwardRef<HTMLDivElement, MessageDayProps>(function MessageDay(
  {
    date,
    messages,
    excerptMessageTsHighlight,
    excerptRefIndexByMessageTs,
    excerptTextsByMessageTs,
    excerptRefsArray,
  },
  ref
) {
  const dayLabel = formatDayLabel(date);

  return (
    <Box ref={ref}>
      <Divider
        my="lg"
        color={DAY_DIVIDER_COLOR}
        label={
          <Text size="sm" fw={700} c="dimmed">
            {dayLabel}
          </Text>
        }
        labelPosition="center"
      />
      {messages
        .slice()
        .sort((a, b) => (a.ts < b.ts ? -1 : 1))
        .map((message) => {
          const isHighlighted = excerptMessageTsHighlight?.has(message.ts) ?? false;
          const refIndex = excerptRefIndexByMessageTs?.get(message.ts);
          const excerptRef =
            refIndex !== undefined && excerptRefsArray
              ? (el: HTMLDivElement | null) => {
                while (excerptRefsArray.current.length <= refIndex) {
                  excerptRefsArray.current.push(null);
                }
                excerptRefsArray.current[refIndex] = el;
              }
              : undefined;
          return (
            <Message
              key={message.ts}
              message={message}
              isHighlighted={isHighlighted}
              excerptRef={excerptRef}
              excerptRefIndexByMessageTs={excerptRefIndexByMessageTs}
              excerptRefsArray={excerptRefsArray}
              excerptTexts={excerptTextsByMessageTs?.get(message.ts)}
              excerptTextsByMessageTs={excerptTextsByMessageTs}
            />
          );
        })}
    </Box>
  );
});
